<template>
  <div class="w-full flex justify-center">
    <div :class="widthClass" class="relative bg-gray-700 border-2 border-gray-500 rounded-lg shadow-lg overflow-hidden h-16">
      <!-- Progress bar fill (fills from left to right) -->
      <div 
        class="absolute inset-0 progress-bar-fill transition-all duration-500 ease-out"
        :class="{ 'indeterminate': progress === null || progress === undefined }"
        :style="progressStyle"
      ></div>
      <!-- Text overlay -->
      <div class="absolute inset-0 flex items-center justify-center">
        <span class="text-xl font-semibold text-white relative z-10">{{ text }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgressBar',
  props: {
    text: {
      type: String,
      default: 'Processing data...'
    },
    width: {
      type: String,
      default: '85%',
      validator: (value) => {
        // Accept Tailwind width classes or percentage strings
        return typeof value === 'string' && (
          value.includes('%') || 
          value.includes('w-') || 
          value === 'full' ||
          value === 'auto'
        )
      }
    },
    progress: {
      type: Number,
      default: null,
      validator: (value) => {
        // Allow null/undefined for indeterminate progress, or 0-100 for determinate
        return value === null || value === undefined || (value >= 0 && value <= 100)
      }
    }
  },
  computed: {
    widthClass() {
      // If it's already a Tailwind class or a percentage, use it directly
      if (this.width.includes('w-') || this.width === 'full' || this.width === 'auto') {
        return this.width
      }
      // If it's a percentage, convert to Tailwind arbitrary value
      if (this.width.includes('%')) {
        return `w-[${this.width}]`
      }
      // Default fallback
      return 'w-[85%]'
    },
    progressStyle() {
      // If progress is provided, fill based on percentage
      if (this.progress !== null && this.progress !== undefined) {
        return {
          width: `${Math.max(0, Math.min(100, this.progress))}%`
        }
      }
      // Indeterminate progress (no width restriction - handled by CSS animation)
      return {}
    }
  }
}
</script>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes indeterminate {
  0% {
    left: -35%;
    right: 100%;
  }
  60% {
    left: 100%;
    right: -90%;
  }
  100% {
    left: 100%;
    right: -90%;
  }
}

.progress-bar-fill {
  background: linear-gradient(
    90deg,
    rgba(59, 130, 246, 0.5) 0%,
    rgba(37, 99, 235, 0.8) 50%,
    rgba(59, 130, 246, 0.5) 100%
  );
  background-size: 200% 100%;
  left: 0;
  top: 0;
  bottom: 0;
  height: 100%;
}

.progress-bar-fill.indeterminate {
  width: auto !important;
  animation: indeterminate 1.5s ease-in-out infinite;
}

.progress-bar-fill:not(.indeterminate) {
  animation: shimmer 2s linear infinite;
}
</style>

